import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import SuggestInput from '../components/SuggestInput';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import {
  Users, UserPlus, CheckCircle, XCircle, Crown, Calendar, ListPlus, MapPinArea,
  PencilSimple, Trash, Buildings, Broom, Pause, ChartLine, PaperPlaneTilt, Copy,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import AdminAccountsPanel from '../components/AdminAccountsPanel';
import ProfessionChips from '../components/ProfessionChips';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';

const emptyCenter = {
  name: '', region: '', province: '', city: '', comune: '', cap: '', address: '',
  latitude: null, longitude: null, phone: '', email: '', description: '', active: true,
  offered_profession_codes: [],
};

const AdminDashboard = ({ section = null }) => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const initialTab = section === 'professionals' ? 'pending'
    : section === 'taxonomy' ? 'needs'
    : section === 'centers' ? 'centers'
    : section === 'tools' ? 'tools' : 'pending';
  const [activeTab, setActiveTab] = useState(initialTab);
  useEffect(() => { setActiveTab(initialTab); }, [initialTab]);
  const [trend, setTrend] = useState([]);
  const [stats, setStats] = useState(null);
  const [pending, setPending] = useState([]);
  const [allProfs, setAllProfs] = useState([]);
  const [needs, setNeeds] = useState([]);
  const [zones, setZones] = useState([]);
  const [centers, setCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newNeed, setNewNeed] = useState('');
  const [editingNeed, setEditingNeed] = useState(null);
  const [centerDialogOpen, setCenterDialogOpen] = useState(false);
  const [inviteLink, setInviteLink] = useState(null);
  const [regionsGeo, setRegionsGeo] = useState([]);
  const [geoProvinces, setGeoProvinces] = useState([]);
  const [comuneCheck, setComuneCheck] = useState(null);
  const [addressCheck, setAddressCheck] = useState(null);
  const [checkingAddress, setCheckingAddress] = useState(false);
  const [centerForm, setCenterForm] = useState(emptyCenter);
  const [adminProfessions, setAdminProfessions] = useState([]);
  useEffect(() => { api.get('/api/professions').then((r) => setAdminProfessions(r.data || [])).catch(() => {}); }, []);
  const [editingCenterId, setEditingCenterId] = useState(null);
  const [cleanupPreview, setCleanupPreview] = useState(null);
  const [selectedCleanupIds, setSelectedCleanupIds] = useState([]);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [cleanupPatterns, setCleanupPatterns] = useState('@test.com,@example.com');
  const [cleanupIncludeAll, setCleanupIncludeAll] = useState(false);
  const [cleanupRole, setCleanupRole] = useState('all');
  const [cleanupSearch, setCleanupSearch] = useState('');
  const [dataCleanupLoading, setDataCleanupLoading] = useState(false);
  const [dataCleanupPreview, setDataCleanupPreview] = useState(null);
  const [reqCleanupPreview, setReqCleanupPreview] = useState(null);
  const [reqCleanupLoading, setReqCleanupLoading] = useState(false);

  useEffect(() => {
    if (!authLoading && user && user.role !== 'admin') {
      toast.error('Accesso riservato agli admin');
      navigate('/');
      return;
    }
    if (user && user.role === 'admin') fetchAll();
  }, [user, authLoading, navigate]);

  const fetchAll = async () => {
    try {
      const [statsRes, pendingRes, allRes, needsRes, zonesRes, centersRes] = await Promise.all([
        api.get('/api/admin/stats'),
        api.get('/api/admin/professionals/pending'),
        api.get('/api/admin/professionals'),
        api.get('/api/admin/needs'),
        api.get('/api/admin/zones'),
        api.get('/api/admin/centers'),
      ]);
      setStats(statsRes.data);
      setPending(pendingRes.data);
      setAllProfs(allRes.data);
      setNeeds(needsRes.data);
      setZones(zonesRes.data);
      setCenters(centersRes.data);
      api.get('/api/geo/italian-regions').then(r => setRegionsGeo(r.data || [])).catch(() => {});
      if (!section) {
        try {
          const an = await api.get('/api/admin/crm/analytics?weeks=12');
          setTrend(an.data?.series || []);
        } catch { /* ignore */ }
      }
    } catch (error) {
      toast.error('Errore caricamento dati admin');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId) => {
    try {
      await api.post(`/api/admin/professionals/${userId}/approve`, { reason: 'Documenti verificati' });
      toast.success('Professionista approvato');
      fetchAll();
    } catch { toast.error('Errore approvazione'); }
  };

  const handleReject = async (userId) => {
    const reason = window.prompt('Motivo del rifiuto:');
    if (!reason) return;
    try {
      await api.post(`/api/admin/professionals/${userId}/reject`, { reason });
      toast.success('Professionista rifiutato');
      fetchAll();
    } catch { toast.error('Errore rifiuto'); }
  };

  const handleSuspend = async (userId) => {
    const reason = window.prompt('Motivo della sospensione:');
    if (!reason) return;
    try {
      await api.post(`/api/admin/professionals/${userId}/suspend`, { reason });
      toast.success('Professionista sospeso');
      fetchAll();
    } catch { toast.error('Errore sospensione'); }
  };

  const handleAddNeed = async (e) => {
    e.preventDefault();
    if (!newNeed.trim()) return;
    try {
      await api.post('/api/admin/needs', { label: newNeed.trim(), active: true, order: needs.length });
      toast.success('Bisogno aggiunto');
      setNewNeed('');
      fetchAll();
    } catch { toast.error('Errore aggiunta'); }
  };

  const handleToggleNeed = async (need) => {
    try { await api.put(`/api/admin/needs/${need.need_id}`, { active: !need.active }); fetchAll(); }
    catch { toast.error('Errore aggiornamento'); }
  };

  const handleDeleteNeed = async (need) => {
    if (!window.confirm(`Eliminare "${need.label}"?`)) return;
    try { await api.delete(`/api/admin/needs/${need.need_id}`); toast.success('Eliminato'); fetchAll(); }
    catch { toast.error('Errore eliminazione'); }
  };

  const handleUpdateNeedLabel = async () => {
    if (!editingNeed) return;
    try {
      await api.put(`/api/admin/needs/${editingNeed.need_id}`, { label: editingNeed.label });
      toast.success('Aggiornato');
      setEditingNeed(null);
      fetchAll();
    } catch { toast.error('Errore aggiornamento'); }
  };

  const handleToggleZone = async (zone) => {
    try { await api.put(`/api/admin/zones/${zone.zone_id}`, { enabled: !zone.enabled }); fetchAll(); }
    catch { toast.error('Errore aggiornamento zona'); }
  };

  const zonesByRegion = zones.reduce((acc, z) => {
    if (!acc[z.region]) acc[z.region] = [];
    acc[z.region].push(z);
    return acc;
  }, {});

  const openNewCenter = () => {
    setCenterForm(emptyCenter);
    setGeoProvinces([]);
    setComuneCheck(null);
    setAddressCheck(null);
    setEditingCenterId(null);
    setCenterDialogOpen(true);
  };

  const pickCenterRegion = (region) => {
    setCenterForm(f => ({ ...f, region, province: '', comune: '', city: '', cap: '', address: '', latitude: null, longitude: null }));
    setGeoProvinces(regionsGeo.find(r => r.region === region)?.provinces || []);
    setComuneCheck(null);
    setAddressCheck(null);
  };

  const fetchCenterComuni = async (q) => {
    const r = await api.get(`/api/geo/comuni?province=${encodeURIComponent(centerForm.province)}&q=${encodeURIComponent(q)}&limit=8`);
    return r.data.items || [];
  };

  const pickCenterComune = (c) => {
    setCenterForm(f => ({
      ...f, comune: c.comune, city: c.comune, address: '', latitude: null, longitude: null,
      cap: c.caps.length === 1 ? c.caps[0] : '',
    }));
    setComuneCheck({ valid: true, comune: c.comune, caps: c.caps });
    setAddressCheck(null);
  };

  const fetchCenterStreets = async (q) => {
    const r = await api.get(`/api/geo/streets?comune=${encodeURIComponent(centerForm.comune)}&q=${encodeURIComponent(q)}`);
    return r.data.items || [];
  };

  const pickCenterStreet = (x) => {
    setCenterForm(f => ({ ...f, address: x.street, cap: x.cap || f.cap, latitude: x.latitude, longitude: x.longitude }));
    setAddressCheck({ valid: true, formatted: x.formatted, cap: x.cap });
  };

  const checkCenterAddress = async () => {
    if (!centerForm.address || !comuneCheck?.valid) { toast.error('Verifica prima il comune e inserisci l\'indirizzo'); return; }
    setCheckingAddress(true);
    try {
      const r = await api.post('/api/geo/validate-address', {
        street: centerForm.address, comune: centerForm.comune, province: centerForm.province, region: centerForm.region,
      });
      setAddressCheck(r.data);
      if (r.data.valid) {
        setCenterForm(f => ({
          ...f,
          cap: r.data.cap && (comuneCheck.caps || []).includes(r.data.cap) ? r.data.cap : f.cap,
          latitude: r.data.latitude, longitude: r.data.longitude,
        }));
        toast.success(`Indirizzo confermato${r.data.cap ? ` · CAP ${r.data.cap}` : ''}`);
      } else {
        toast.warning('Indirizzo non confermato: controlla la via o scegli il CAP dall\u2019elenco');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore verifica indirizzo');
    } finally { setCheckingAddress(false); }
  };

  const openEditCenter = (c) => {
    setCenterForm({
      name: c.name || '', region: c.region || '', province: c.province || '', city: c.city || '',
      comune: c.comune || c.city || '', cap: c.cap || '',
      latitude: c.latitude ?? null, longitude: c.longitude ?? null,
      address: c.address || '', phone: c.phone || '', email: c.email || '',
      description: c.description || '', active: !!c.active,
      offered_profession_codes: c.offered_profession_codes || [],
    });
    setGeoProvinces((regionsGeo.find(r => r.region === c.region)?.provinces) || []);
    setComuneCheck(c.comune || c.city ? { valid: true, caps: c.cap ? [c.cap] : [] } : null);
    setAddressCheck(null);
    setEditingCenterId(c.center_id);
    setCenterDialogOpen(true);
  };

  const handleSaveCenter = async (e) => {
    e.preventDefault();
    if (!centerForm.name.trim() || !centerForm.region.trim() || !centerForm.province.trim()) {
      toast.error('Nome, regione e provincia sono obbligatori');
      return;
    }
    if (!centerForm.comune.trim() || !centerForm.cap) {
      toast.error('Scegli il comune dai suggerimenti e il CAP della zona');
      return;
    }
    const payload = { ...centerForm };
    if (!payload.email) delete payload.email;
    try {
      if (editingCenterId) {
        await api.put(`/api/admin/centers/${editingCenterId}`, payload);
        toast.success('Centro aggiornato');
      } else {
        const res = await api.post('/api/admin/centers', payload);
        const inv = res.data?.invite;
        if (inv) {
          setInviteLink({ name: payload.name, link: inv.invite_link, email: inv.email, email_sent: inv.email_sent });
          toast.success(inv.email_sent ? 'Centro creato: invito di accesso inviato via email' : 'Centro creato: email non inviata, copia il link di accesso');
        } else {
          toast.success(payload.email ? 'Centro creato' : 'Centro creato: aggiungi l\'email per inviare l\'accesso');
        }
      }
      setCenterDialogOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore salvataggio centro');
    }
  };

  const handleSendCenterInvite = async (c) => {
    try {
      const res = await api.post(`/api/admin/centers/${c.center_id}/invite`);
      setInviteLink({ name: c.name, link: res.data.invite_link, email: res.data.email, email_sent: res.data.email_sent });
      toast.success(res.data.email_sent ? 'Invito di accesso inviato' : 'Invito creato: email non inviata, copia il link');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore invio invito');
    }
  };

  const handleDeleteCenter = async (c) => {
    if (!window.confirm(`Eliminare il centro "${c.name}"?`)) return;
    try {
      await api.delete(`/api/admin/centers/${c.center_id}`);
      toast.success('Centro eliminato');
      fetchAll();
    } catch { toast.error('Errore eliminazione'); }
  };

  const handleToggleCenter = async (c) => {
    try {
      await api.put(`/api/admin/centers/${c.center_id}`, { active: !c.active });
      fetchAll();
    } catch { toast.error('Errore aggiornamento'); }
  };

  const cleanupPayload = () => ({
    email_patterns: cleanupPatterns.split(',').map(p => p.trim()).filter(Boolean),
    include_all: cleanupIncludeAll,
    role: cleanupRole === 'all' ? null : cleanupRole,
    search: cleanupSearch.trim() || null,
  });

  const runCleanupDryRun = async () => {
    setCleanupLoading(true);
    try {
      const res = await api.post('/api/admin/cleanup', { dry_run: true, ...cleanupPayload() });
      setCleanupPreview(res.data);
      setSelectedCleanupIds((res.data.matched_users || []).map(u => u.user_id));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore anteprima cleanup');
    } finally { setCleanupLoading(false); }
  };

  const toggleCleanupId = (id) => setSelectedCleanupIds(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  const runCleanupConfirm = async () => {
    if (selectedCleanupIds.length === 0) return toast.error('Seleziona almeno un utente');
    if (!window.confirm(`Eliminare definitivamente ${selectedCleanupIds.length} utenti selezionati? L'azione è irreversibile.`)) return;
    setCleanupLoading(true);
    try {
      const res = await api.post('/api/admin/cleanup', { dry_run: false, confirm: true, ...cleanupPayload(), user_ids: selectedCleanupIds });
      toast.success(`Eliminati ${res.data.deleted_count} utenti`);
      setCleanupPreview(null);
      setSelectedCleanupIds([]);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore cleanup');
    } finally { setCleanupLoading(false); }
  };

  const runReqCleanup = async (mode, dryRun) => {
    if (!dryRun) {
      const label = mode === 'all' ? 'TUTTE le richieste'
        : mode === 'orphans' ? 'tutte le richieste di utenti cancellati (orfane)'
        : `${reqCleanupPreview?.matched_count || 0} richieste vuote/di test`;
      if (!window.confirm(`Eliminare definitivamente ${label}? L'azione è irreversibile.`)) return;
    }
    setReqCleanupLoading(true);
    try {
      const res = await api.post('/api/admin/cleanup/requests', { mode, dry_run: dryRun, confirm: !dryRun });
      if (dryRun) {
        setReqCleanupPreview(res.data);
      } else {
        toast.success(`Eliminate ${res.data.deleted_count} richieste`);
        setReqCleanupPreview(null);
        fetchAll();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore pulizia richieste');
    } finally { setReqCleanupLoading(false); }
  };

  const [orphanPreview, setOrphanPreview] = useState(null);
  const [orphanLoading, setOrphanLoading] = useState(false);
  const orphanRelatedTotal = Object.values(orphanPreview?.related || {}).reduce((a, b) => a + b, 0);

  const runOrphanCleanup = async (dryRun) => {
    setOrphanLoading(true);
    try {
      const res = await api.post(`/api/admin/cleanup/orphan-professionals?dry_run=${dryRun}`);
      const related = Object.values(res.data.related || {}).reduce((a, b) => a + b, 0);
      if (!dryRun) {
        toast.success(`Rimossi ${res.data.professionals_deleted} profili orfani e ${related} dati collegati`);
        setOrphanPreview({ found: 0, orphans: [], related: {} });
        fetchAll();
      } else {
        setOrphanPreview(res.data);
        if (res.data.found === 0 && related === 0) toast.success('Nessun dato residuo trovato');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore pulizia profili orfani');
    } finally { setOrphanLoading(false); }
  };

  const runDataCleanup = async (target, mode, dryRun) => {    const labels = {
      bookings: { test: 'le prenotazioni di test', orphans: 'le prenotazioni di utenti cancellati', all: 'TUTTE le prenotazioni' },
      payments: { unpaid: 'i pagamenti non incassati', orphans: 'i pagamenti di utenti cancellati', all: 'TUTTI i pagamenti' },
    };
    if (!dryRun && !window.confirm(`Eliminare definitivamente ${labels[target][mode]}? L'azione è irreversibile.`)) return;
    setDataCleanupLoading(true);
    try {
      const res = await api.post(`/api/admin/cleanup/${target}`, { mode, dry_run: dryRun, confirm: !dryRun });
      if (dryRun) {
        setDataCleanupPreview({ target, ...res.data });
        toast.info(`${res.data.matched_count} elementi trovati`);
      } else {
        toast.success(`Eliminati ${res.data.deleted_count} elementi`);
        setDataCleanupPreview(null);
        fetchAll();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore pulizia dati');
    } finally { setDataCleanupLoading(false); }
  };


  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 py-12">
        {!section && (
        <div className="mb-8">
          <h1 className="font-heading font-semibold text-4xl text-foreground mb-2" data-testid="admin-dashboard-title">
            Admin Dashboard
          </h1>
          <p className="text-foreground/70">Panoramica della piattaforma VivaLink Italia</p>
        </div>
        )}

        {!section && stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card className="neumorphism rounded-2xl p-5" data-testid="stat-users">
              <div className="flex items-center gap-3 mb-2">
                <Users size={20} className="text-primary" weight="duotone" />
                <p className="text-xs text-foreground/60 uppercase tracking-wider">Utenti</p>
              </div>
              <p className="text-3xl font-heading font-bold">{stats.users.total}</p>
            </Card>
            <Card className="neumorphism rounded-2xl p-5" data-testid="stat-professionals">
              <div className="flex items-center gap-3 mb-2">
                <UserPlus size={20} className="text-accent" weight="duotone" />
                <p className="text-xs text-foreground/60 uppercase tracking-wider">Professionisti</p>
              </div>
              <p className="text-3xl font-heading font-bold">{stats.professionals.approved}</p>
              <p className="text-xs text-foreground/60 mt-1">
                {stats.professionals.pending} in attesa • {stats.professionals.suspended || 0} sospesi
              </p>
            </Card>
            <Card className="neumorphism rounded-2xl p-5" data-testid="stat-premium">
              <div className="flex items-center gap-3 mb-2">
                <Crown size={20} className="text-accent" weight="duotone" />
                <p className="text-xs text-foreground/60 uppercase tracking-wider">Premium</p>
              </div>
              <p className="text-3xl font-heading font-bold">{stats.professionals.premium}</p>
            </Card>
            <Card className="neumorphism rounded-2xl p-5" data-testid="stat-bookings">
              <div className="flex items-center gap-3 mb-2">
                <Calendar size={20} className="text-primary" weight="duotone" />
                <p className="text-xs text-foreground/60 uppercase tracking-wider">Prenotazioni</p>
              </div>
              <p className="text-3xl font-heading font-bold">{stats.bookings.total}</p>
            </Card>
          </div>
        )}

        {!section && trend.length > 0 && (
          <Card className="rounded-2xl p-5 mb-6" data-testid="dashboard-trend-chart">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <p className="font-heading font-medium text-foreground flex items-center gap-2"><ChartLine size={20} weight="duotone" className="text-primary" /> Andamento — nuove richieste e prenotazioni per settimana</p>
              <button onClick={() => navigate('/admin/analytics')} className="text-sm text-primary hover:underline" data-testid="dashboard-open-analytics">
                Analytics dettagliate →
              </button>
            </div>
            <ResponsiveContainer width="100%" height={240}>
              <ComposedChart data={trend} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E3F2FD" />
                <XAxis dataKey="week_label" tick={{ fontSize: 12, fill: '#5B7A96' }} />
                <YAxis tick={{ fontSize: 12, fill: '#5B7A96' }} />
                <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #E3F2FD' }} />
                <Legend />
                <Bar dataKey="requests" name="Richieste" fill="#1565C0" radius={[6, 6, 0, 0]} barSize={16} />
                <Bar dataKey="bookings" name="Prenotazioni" fill="#2CA9E1" radius={[6, 6, 0, 0]} barSize={16} />
                <Line type="monotone" dataKey="new_users" name="Nuovi clienti" stroke="#0D3B66" strokeWidth={2} dot={{ r: 2 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </Card>
        )}

        {!section && (
          <div data-testid="admin-overview-links">
            <div className="mb-4 flex items-center justify-between flex-wrap gap-2">
              <h2 className="font-heading font-semibold text-lg text-foreground">Gestione rapida</h2>
              {stats && stats.professionals.pending > 0 && (
                <button
                  onClick={() => navigate('/admin/professionisti')}
                  data-testid="overview-pending-alert"
                  className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-sm font-medium hover:bg-amber-100 transition-colors"
                >
                  <UserPlus size={16} weight="duotone" /> {stats.professionals.pending} professionisti in attesa di approvazione
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { to: '/admin/professionisti', label: 'Professionisti', desc: 'Approvazioni ed elenco', icon: UserPlus },
                { to: '/admin/pipeline', label: 'Pipeline', desc: 'Richieste per stato', icon: Buildings },
                { to: '/admin/pagamenti', label: 'Pagamenti', desc: 'Transazioni e incassi', icon: Crown },
                { to: '/admin/clienti', label: 'Clienti', desc: 'Elenco e storico richieste', icon: Users },
                { to: '/admin/messaggi', label: 'Comunicazioni', desc: 'Chat cliente-professionista', icon: Calendar },
                { to: '/admin/centri', label: 'Centri', desc: 'Centri di assistenza', icon: Buildings },
                { to: '/admin/taxonomy', label: 'Tassonomia', desc: 'Professioni, servizi, config', icon: Buildings },
                { to: '/admin/bisogni-zone', label: 'Bisogni & Zone', desc: 'Bisogni assistenziali e province', icon: Buildings },
                { to: '/admin/zone-scoperte', label: 'Zone scoperte', desc: 'Comuni senza Centri e richieste in arrivo', icon: Buildings },
                { to: '/admin/prestazioni-proposte', label: 'Prestazioni proposte', desc: 'Nuove prestazioni suggerite dai professionisti', icon: Buildings },
                { to: '/admin/import-export', label: 'Import / Export', desc: 'CSV, Excel, PDF', icon: Buildings },
                { to: '/admin/strumenti', label: 'Strumenti', desc: 'Pulizia dati, promemoria', icon: Buildings },
              ].map((q) => (
                <button
                  key={q.to}
                  onClick={() => navigate(q.to)}
                  data-testid={`overview-link-${q.to.replace(/\//g, '-').replace(/^-/, '')}`}
                  className="text-left bg-white rounded-2xl border border-[#E3F2FD] hover:border-primary/40 hover:shadow-[0_6px_20px_rgba(21,101,192,0.08)] transition-all p-4 flex items-center gap-3"
                >
                  <span className="w-11 h-11 rounded-xl bg-secondary text-primary flex items-center justify-center shrink-0">
                    <q.icon size={22} weight="duotone" />
                  </span>
                  <span className="min-w-0">
                    <span className="block font-heading font-medium text-foreground">{q.label}</span>
                    <span className="block text-xs text-foreground/60">{q.desc}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {section && (
        <Card className="neumorphism rounded-2xl p-6" data-testid="admin-section-card">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            {section === 'professionals' && (
            <TabsList data-testid="admin-tabs" className="flex flex-wrap gap-2">
              <TabsTrigger value="pending">
                Approvazioni {pending.length > 0 && <Badge className="ml-2 bg-accent text-white">{pending.length}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="all">Tutti i professionisti</TabsTrigger>
            </TabsList>
            )}
            {section === 'taxonomy' && (
            <TabsList data-testid="admin-tabs" className="flex flex-wrap gap-2">
              <TabsTrigger value="needs">Bisogni</TabsTrigger>
              <TabsTrigger value="zones">Zone</TabsTrigger>
            </TabsList>
            )}

            {/* PENDING */}
            <TabsContent value="pending" className="mt-6">
              {pending.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle size={48} className="text-primary mx-auto mb-4" weight="duotone" />
                  <p className="text-foreground/70">Nessuna approvazione in sospeso</p>
                </div>
              ) : (
                <div className="space-y-3" data-testid="pending-list">
                  {pending.map((p) => (
                    <div key={p.user_id} className="border border-border rounded-xl p-4 flex items-center justify-between flex-wrap gap-4" data-testid={`pending-${p.user_id}`}>
                      <div>
                        <p className="font-heading font-medium text-lg">{p.name}</p>
                        <p className="text-sm text-foreground/70">{p.email}</p>
                        <p className="text-xs text-foreground/60 mt-1">Registrato: {new Date(p.created_at).toLocaleDateString('it-IT')}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => handleReject(p.user_id)} data-testid={`reject-${p.user_id}`}>
                          <XCircle size={16} className="mr-1" /> Rifiuta
                        </Button>
                        <Button size="sm" onClick={() => handleApprove(p.user_id)} data-testid={`approve-${p.user_id}`}>
                          <CheckCircle size={16} className="mr-1" /> Approva
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* ALL PROFS */}
            <TabsContent value="all" className="mt-6">
              <div className="space-y-3" data-testid="all-professionals-list">
                {allProfs.map((p) => (
                  <div key={p.user_id} className="border border-border rounded-xl p-4 flex items-center justify-between flex-wrap gap-4">
                    <div>
                      <p className="font-heading font-medium">{p.name}</p>
                      <p className="text-sm text-foreground/70">{p.email}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge className={
                        p.professional_status === 'approved' ? 'bg-sky-100 text-sky-800' :
                        p.professional_status === 'rejected' ? 'bg-red-100 text-red-800' :
                        p.professional_status === 'suspended' ? 'bg-orange-100 text-orange-800' :
                        'bg-yellow-100 text-yellow-800'
                      }>
                        {p.professional_status || 'unknown'}
                      </Badge>
                      {p.professional_status === 'approved' && (
                        <Button size="sm" variant="outline" onClick={() => handleSuspend(p.user_id)} data-testid={`suspend-${p.user_id}`}>
                          <Pause size={14} className="mr-1" /> Sospendi
                        </Button>
                      )}
                      {p.professional_status === 'suspended' && (
                        <Button size="sm" onClick={() => handleApprove(p.user_id)} data-testid={`reactivate-${p.user_id}`}>
                          <CheckCircle size={14} className="mr-1" /> Riattiva
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>

            {/* NEEDS */}
            <TabsContent value="needs" className="mt-6" data-testid="needs-tab">
              <div className="mb-6">
                <form onSubmit={handleAddNeed} className="flex gap-3">
                  <Input value={newNeed} onChange={(e) => setNewNeed(e.target.value)} placeholder="Aggiungi nuovo bisogno assistenziale..." data-testid="new-need-input" />
                  <Button type="submit" className="rounded-full" data-testid="add-need-btn">
                    <ListPlus size={18} className="mr-1" /> Aggiungi
                  </Button>
                </form>
              </div>
              <div className="space-y-2" data-testid="needs-list">
                {needs.map((n) => (
                  <div key={n.need_id} className="border border-border rounded-xl p-3 flex items-center justify-between flex-wrap gap-3" data-testid={`need-${n.need_id}`}>
                    {editingNeed?.need_id === n.need_id ? (
                      <div className="flex gap-2 flex-1">
                        <Input value={editingNeed.label} onChange={(e) => setEditingNeed({ ...editingNeed, label: e.target.value })} />
                        <Button size="sm" onClick={handleUpdateNeedLabel}>Salva</Button>
                        <Button size="sm" variant="outline" onClick={() => setEditingNeed(null)}>Annulla</Button>
                      </div>
                    ) : (
                      <>
                        <div className="flex-1">
                          <p className={`font-medium ${!n.active ? 'text-foreground/40 line-through' : ''}`}>{n.label}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-2">
                            <Switch checked={n.active} onCheckedChange={() => handleToggleNeed(n)} data-testid={`toggle-need-${n.need_id}`} />
                            <span className="text-xs text-foreground/60">{n.active ? 'Attivo' : 'Off'}</span>
                          </div>
                          <Button size="sm" variant="ghost" onClick={() => setEditingNeed({ ...n })} data-testid={`edit-need-${n.need_id}`}>
                            <PencilSimple size={16} />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDeleteNeed(n)} data-testid={`delete-need-${n.need_id}`}>
                            <Trash size={16} className="text-destructive" />
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </TabsContent>

            {/* ZONES */}
            <TabsContent value="zones" className="mt-6" data-testid="zones-tab">
              <div className="mb-4 flex items-center gap-2 text-sm text-foreground/70">
                <MapPinArea size={20} className="text-primary" weight="duotone" />
                Abilita/disabilita le province dove ricevere richieste
              </div>
              <div className="space-y-4">
                {Object.entries(zonesByRegion).sort().map(([region, provinces]) => {
                  const enabledCount = provinces.filter(p => p.enabled).length;
                  return (
                    <details key={region} className="border border-border rounded-xl p-4" open={enabledCount > 0} data-testid={`region-${region}`}>
                      <summary className="cursor-pointer font-heading font-medium flex items-center justify-between">
                        <span>{region}</span>
                        <Badge className={enabledCount > 0 ? 'bg-primary text-white' : 'bg-muted text-foreground/60'}>
                          {enabledCount}/{provinces.length} attive
                        </Badge>
                      </summary>
                      <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2">
                        {provinces.map(z => (
                          <label key={z.zone_id} className={`flex items-center justify-between p-2 rounded-lg text-sm cursor-pointer ${z.enabled ? 'bg-primary/5' : 'bg-muted/50'}`} data-testid={`zone-${z.zone_id}`}>
                            <span>{z.province}</span>
                            <Switch checked={z.enabled} onCheckedChange={() => handleToggleZone(z)} data-testid={`toggle-zone-${z.zone_id}`} />
                          </label>
                        ))}
                      </div>
                    </details>
                  );
                })}
              </div>
            </TabsContent>

            {/* CENTERS */}
            <TabsContent value="centers" className="mt-6" data-testid="centers-tab">
              <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <div className="flex items-center gap-2 text-sm text-foreground/70">
                  <Buildings size={20} className="text-primary" weight="duotone" />
                  Gestione Centri Assistenza Viva
                </div>
                <Dialog open={centerDialogOpen} onOpenChange={setCenterDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="rounded-full" onClick={openNewCenter} data-testid="new-center-btn">
                      <ListPlus size={18} className="mr-1" /> Nuovo Centro
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>{editingCenterId ? 'Modifica Centro' : 'Nuovo Centro'}</DialogTitle>
                      <DialogDescription>Centro Assistenza Viva - dati identificativi e contatti</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleSaveCenter} className="space-y-3">
                      <Input placeholder="Nome centro *" value={centerForm.name} onChange={e => setCenterForm({ ...centerForm, name: e.target.value })} data-testid="center-name-input" required />
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Regione *</Label>
                          <Select value={centerForm.region} onValueChange={pickCenterRegion}>
                            <SelectTrigger data-testid="center-region-select"><SelectValue placeholder="Scegli regione" /></SelectTrigger>
                            <SelectContent>
                              {regionsGeo.map(r => <SelectItem key={r.region} value={r.region}>{r.region}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Provincia *</Label>
                          <Select
                            value={centerForm.province}
                            onValueChange={(v) => {
                              setCenterForm(f => ({ ...f, province: v, comune: '', city: '', cap: '', address: '', latitude: null, longitude: null }));
                              setComuneCheck(null); setAddressCheck(null);
                            }}
                            disabled={!centerForm.region}
                          >
                            <SelectTrigger data-testid="center-province-select"><SelectValue placeholder="Scegli provincia" /></SelectTrigger>
                            <SelectContent>
                              {geoProvinces.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Comune * (verificato)</Label>
                        <SuggestInput
                          value={centerForm.comune}
                          onChange={(v) => { setCenterForm(f => ({ ...f, comune: v, city: v, cap: '', address: '', latitude: null, longitude: null })); setComuneCheck(null); setAddressCheck(null); }}
                          onPick={pickCenterComune}
                          fetcher={fetchCenterComuni}
                          renderItem={(c) => `${c.comune} (${c.sigla}) — ${c.caps.length} CAP`}
                          placeholder={centerForm.province ? 'Es. Milano' : 'Scegli prima la provincia'}
                          disabled={!centerForm.province}
                          testId="center-comune-input"
                          emptyHint="Nessun comune con questo nome nella provincia scelta"
                        />
                        {comuneCheck?.valid && (
                          <p className="text-xs text-emerald-700" data-testid="center-comune-check">
                            Comune confermato{comuneCheck.caps?.length ? ` (${comuneCheck.caps.length} CAP)` : ''}
                          </p>
                        )}
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Indirizzo (verificato)</Label>
                        <div className="flex gap-2">
                          <div className="flex-1">
                            <SuggestInput
                              value={centerForm.address}
                              onChange={(v) => { setCenterForm(f => ({ ...f, address: v })); setAddressCheck(null); }}
                              onPick={pickCenterStreet}
                              fetcher={fetchCenterStreets}
                              renderItem={(x) => x.formatted}
                              placeholder={comuneCheck?.valid ? 'Es. Corso Buenos Aires 10' : 'Verifica prima il comune'}
                              disabled={!comuneCheck?.valid}
                              testId="center-address-input"
                              minChars={3}
                              debounceMs={700}
                              emptyHint="Nessuna via trovata: controlla come l'hai scritta"
                            />
                          </div>
                          <Button type="button" variant="outline" className="rounded-full" onClick={checkCenterAddress} disabled={checkingAddress || !comuneCheck?.valid} data-testid="center-check-address-btn">
                            {checkingAddress ? 'Verifica…' : 'Verifica'}
                          </Button>
                        </div>
                        {addressCheck && (
                          <p className={`text-xs ${addressCheck.valid ? 'text-emerald-700' : 'text-amber-800'}`} data-testid="center-address-check">
                            {addressCheck.valid ? `Indirizzo confermato: ${addressCheck.formatted}` : 'Indirizzo non confermato: scegli il CAP dall\u2019elenco'}
                          </p>
                        )}
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">CAP * (zona di assegnazione)</Label>
                        <Select value={centerForm.cap} onValueChange={(v) => setCenterForm({ ...centerForm, cap: v })} disabled={!comuneCheck?.valid}>
                          <SelectTrigger data-testid="center-cap-select"><SelectValue placeholder="CAP" /></SelectTrigger>
                          <SelectContent>
                            {(comuneCheck?.caps || []).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-foreground/60">Rilevato dall'indirizzo: è la zona di partenza del centro.</p>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <Input placeholder="Telefono" value={centerForm.phone} onChange={e => setCenterForm({ ...centerForm, phone: e.target.value })} data-testid="center-phone-input" />
                        <Input type="email" placeholder="Email" value={centerForm.email} onChange={e => setCenterForm({ ...centerForm, email: e.target.value })} data-testid="center-email-input" />
                      </div>
                      <Textarea placeholder="Descrizione (opzionale)" value={centerForm.description} onChange={e => setCenterForm({ ...centerForm, description: e.target.value })} data-testid="center-description-input" />
                      <div className="space-y-1.5">
                        <div className="text-sm font-medium">Professioni offerte</div>
                        <p className="text-xs text-foreground/60">Il Centro compare nelle ricerche per queste prestazioni appena attiva l'account, anche senza organico.</p>
                        <ProfessionChips professions={adminProfessions} value={centerForm.offered_profession_codes}
                          onChange={(v) => setCenterForm(f => ({ ...f, offered_profession_codes: v }))} testPrefix="center-form" />
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch checked={centerForm.active} onCheckedChange={v => setCenterForm({ ...centerForm, active: v })} data-testid="center-active-switch" />
                        <span className="text-sm text-foreground/70">{centerForm.active ? 'Attivo' : 'Inattivo'}</span>
                      </div>
                      <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => setCenterDialogOpen(false)}>Annulla</Button>
                        <Button type="submit" data-testid="save-center-btn">Salva</Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              {inviteLink && (
                <div className="mb-5 rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="center-invite-link-box">
                  <p className="text-sm font-medium text-amber-900">
                    Link di accesso per {inviteLink.name} ({inviteLink.email})
                    {inviteLink.email_sent ? ' — email inviata' : ' — email non inviata: invialo tu'}
                  </p>
                  <p className="text-xs text-amber-800/80 mt-1">Valido 48 ore, utilizzabile una sola volta.</p>
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    <Input readOnly value={inviteLink.link} className="flex-1 min-w-[240px] text-xs" data-testid="center-invite-link-input" />
                    <Button
                      size="sm" variant="outline" className="rounded-full"
                      onClick={() => { navigator.clipboard.writeText(inviteLink.link); toast.success('Link copiato'); }}
                      data-testid="copy-center-invite-link"
                    >
                      <Copy size={14} className="mr-1" /> Copia
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setInviteLink(null)} data-testid="close-center-invite-link">Chiudi</Button>
                  </div>
                </div>
              )}

              {centers.length === 0 ? (
                <div className="text-center py-12">
                  <Buildings size={48} className="text-foreground/30 mx-auto mb-4" weight="duotone" />
                  <p className="text-foreground/70">Nessun centro configurato</p>
                </div>
              ) : (
                <div className="space-y-3" data-testid="centers-list">
                  {centers.map(c => (
                    <div key={c.center_id} className="border border-border rounded-xl p-4 flex items-start justify-between flex-wrap gap-3" data-testid={`center-${c.center_id}`}>
                      <div className="flex-1 min-w-[200px]">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-heading font-medium text-lg">{c.name}</p>
                          <Badge className={c.active ? 'bg-sky-100 text-sky-800' : 'bg-gray-100 text-gray-600'}>
                            {c.active ? 'Attivo' : 'Inattivo'}
                          </Badge>
                          <Badge
                            data-testid={`center-account-status-${c.center_id}`}
                            className={
                              c.account_status === 'active' ? 'bg-emerald-100 text-emerald-800'
                                : c.account_status === 'invited' ? 'bg-amber-100 text-amber-800'
                                  : c.account_status === 'expired' ? 'bg-orange-100 text-orange-800'
                                    : 'bg-rose-100 text-rose-800'
                            }
                          >
                            {c.account_status === 'active' ? 'Accesso attivo'
                              : c.account_status === 'invited' ? 'Invito inviato'
                                : c.account_status === 'expired' ? 'Invito scaduto'
                                  : 'Senza accesso'}
                          </Badge>
                          {c.account_status !== 'active' && (
                            <Badge className="bg-sky-100 text-sky-800 hover:bg-sky-100" data-testid={`center-visible-pending-${c.center_id}`}>
                              Visibile ai clienti · richieste all'admin finché non attiva
                            </Badge>
                          )}
                        </div>
                        {c.email_owner_role && (
                          <p className="text-xs text-amber-800 mt-1" data-testid={`center-email-owner-${c.center_id}`}>
                            L'email è già di un account {c.email_owner_role === 'professional' ? 'professionista' : 'cliente'}: con l'invito quell'account diventerà il Centro.
                          </p>
                        )}
                        <p className="text-sm text-foreground/70 mt-1">
                          {c.region} • {c.province}{c.city ? ` • ${c.city}` : ''}{c.cap ? ` • CAP ${c.cap}` : ''}
                        </p>
                        {c.address && <p className="text-xs text-foreground/60 mt-1">{c.address}</p>}
                        {(c.phone || c.email) && (
                          <p className="text-xs text-foreground/60 mt-1">
                            {c.phone}{c.phone && c.email ? ' • ' : ''}{c.email}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {c.account_status !== 'active' && (
                          <Button
                            size="sm" variant="outline" className="rounded-full"
                            onClick={() => handleSendCenterInvite(c)}
                            disabled={!c.email}
                            title={c.email ? 'Invia al centro il link per impostare la password' : 'Aggiungi prima l\'email del centro'}
                            data-testid={`invite-center-${c.center_id}`}
                          >
                            <PaperPlaneTilt size={14} className="mr-1" />
                            {c.account_status === 'none' ? 'Invia accesso' : 'Rinvia accesso'}
                          </Button>
                        )}
                        <Switch checked={c.active} onCheckedChange={() => handleToggleCenter(c)} data-testid={`toggle-center-${c.center_id}`} />
                        <Button size="sm" variant="ghost" onClick={() => openEditCenter(c)} data-testid={`edit-center-${c.center_id}`}>
                          <PencilSimple size={16} />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDeleteCenter(c)} data-testid={`delete-center-${c.center_id}`}>
                          <Trash size={16} className="text-destructive" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* TOOLS - CLEANUP */}
            <TabsContent value="tools" className="mt-6" data-testid="tools-tab">
              <div className="space-y-6">
                <AdminAccountsPanel />
                <div className="border border-border rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Broom size={22} className="text-accent" weight="duotone" />
                    <h3 className="font-heading font-medium text-lg">Pulizia Utenti Test</h3>
                  </div>
                  <p className="text-sm text-foreground/70 mb-4">
                    Rimuovi utenti che matchano determinati pattern email (utenti di test/demo). Gli admin non vengono mai eliminati.
                    Il cleanup elimina anche profili professionista, richieste e prenotazioni collegate.
                  </p>
                  <div className="space-y-3">
                    <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="cleanup-include-all-label">
                      <Checkbox checked={cleanupIncludeAll} onCheckedChange={(v) => setCleanupIncludeAll(!!v)} data-testid="cleanup-include-all" />
                      <span>Mostra <strong>TUTTI</strong> gli utenti (ignora filtro email)</span>
                    </label>
                    <div className={cleanupIncludeAll ? 'opacity-40 pointer-events-none' : ''}>
                      <label className="text-xs font-medium text-foreground/70 mb-1 block">Pattern email (separati da virgola)</label>
                      <Input value={cleanupPatterns} onChange={e => setCleanupPatterns(e.target.value)} data-testid="cleanup-patterns-input" placeholder="@test.com,@example.com" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-foreground/70 mb-1 block">Ruolo</label>
                      <div className="flex gap-2">
                        {[{ v: 'all', l: 'Tutti' }, { v: 'user', l: 'Clienti' }, { v: 'professional', l: 'Professionisti' }].map(o => (
                          <button
                            key={o.v}
                            type="button"
                            onClick={() => setCleanupRole(o.v)}
                            data-testid={`cleanup-role-${o.v}`}
                            className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${cleanupRole === o.v ? 'bg-primary text-white border-primary' : 'border-border text-foreground/70 hover:bg-muted'}`}
                          >{o.l}</button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-foreground/70 mb-1 block">Ricerca nome o email</label>
                      <Input value={cleanupSearch} onChange={e => setCleanupSearch(e.target.value)} data-testid="cleanup-search-input" placeholder="es. mario o gmail.com" />
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <Button onClick={runCleanupDryRun} variant="outline" disabled={cleanupLoading} data-testid="cleanup-preview-btn">
                        Anteprima
                      </Button>
                      {cleanupPreview && cleanupPreview.matched_count > 0 && (
                        <Button onClick={runCleanupConfirm} disabled={cleanupLoading || selectedCleanupIds.length === 0} className="bg-destructive hover:bg-destructive/90 text-white" data-testid="cleanup-confirm-btn">
                          <Trash size={16} className="mr-1" /> Elimina {selectedCleanupIds.length} selezionati
                        </Button>
                      )}
                    </div>
                    {cleanupPreview && (
                      <div className="mt-3 bg-muted/40 rounded-lg p-3 max-h-60 overflow-auto" data-testid="cleanup-preview">
                        {cleanupPreview.matched_count === 0 ? (
                          <p className="text-sm font-medium">Nessun utente trovato</p>
                        ) : (
                          <>
                            <div className="flex items-center justify-between mb-2">
                              <p className="text-sm font-medium">{cleanupPreview.matched_count} utenti trovati · {selectedCleanupIds.length} selezionati</p>
                              <div className="flex gap-2">
                                <button type="button" className="text-xs text-primary hover:underline" onClick={() => setSelectedCleanupIds((cleanupPreview.matched_users || []).map(u => u.user_id))} data-testid="cleanup-select-all">Tutti</button>
                                <button type="button" className="text-xs text-foreground/60 hover:underline" onClick={() => setSelectedCleanupIds([])} data-testid="cleanup-select-none">Nessuno</button>
                              </div>
                            </div>
                            {(cleanupPreview.matched_users || []).map(u => (
                              <label key={u.user_id} className="flex items-center gap-2 text-xs text-foreground/80 py-1 cursor-pointer" data-testid={`cleanup-user-${u.user_id}`}>
                                <Checkbox checked={selectedCleanupIds.includes(u.user_id)} onCheckedChange={() => toggleCleanupId(u.user_id)} />
                                <span>{u.name ? `${u.name} — ` : ''}{u.email} <span className="text-foreground/50">({u.role})</span></span>
                              </label>
                            ))}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* REQUESTS CLEANUP */}
                <div className="border border-border rounded-xl p-5" data-testid="req-cleanup-panel">
                  <div className="flex items-center gap-2 mb-3">
                    <Broom size={22} className="text-destructive" weight="duotone" />
                    <h3 className="font-heading font-medium text-lg">Pulizia Richieste</h3>
                  </div>
                  <p className="text-sm text-foreground/70 mb-4">
                    Elimina le richieste vuote/di test (senza regione, bisogni, paziente e telefono) oppure svuota completamente l'archivio richieste. Azione irreversibile.
                  </p>
                  <div className="flex gap-2 flex-wrap">
                    <Button onClick={() => runReqCleanup('empty', true)} variant="outline" disabled={reqCleanupLoading} data-testid="req-cleanup-preview-empty">
                      Anteprima vuote/test
                    </Button>
                    {reqCleanupPreview && reqCleanupPreview.mode === 'empty' && reqCleanupPreview.matched_count > 0 && (
                      <Button onClick={() => runReqCleanup('empty', false)} disabled={reqCleanupLoading} className="bg-destructive hover:bg-destructive/90 text-white" data-testid="req-cleanup-confirm-empty">
                        <Trash size={16} className="mr-1" /> Elimina {reqCleanupPreview.matched_count} vuote/test
                      </Button>
                    )}
                    <Button onClick={() => runReqCleanup('all', false)} variant="outline" disabled={reqCleanupLoading} className="border-destructive text-destructive hover:bg-destructive/10" data-testid="req-cleanup-all">
                      <Trash size={16} className="mr-1" /> Elimina TUTTE le richieste
                    </Button>
                    <Button onClick={() => runReqCleanup('orphans', false)} variant="outline" disabled={reqCleanupLoading} data-testid="req-cleanup-orphans">
                      <Trash size={16} className="mr-1" /> Elimina richieste orfane (utenti cancellati)
                    </Button>
                  </div>
                  {reqCleanupPreview && reqCleanupPreview.mode === 'empty' && (
                    <div className="mt-3 bg-muted/40 rounded-lg p-3 text-sm" data-testid="req-cleanup-preview">
                      {reqCleanupPreview.matched_count === 0 ? 'Nessuna richiesta vuota/di test trovata' : `${reqCleanupPreview.matched_count} richieste vuote/di test da eliminare`}
                    </div>
                  )}
                </div>

                {/* ORPHAN PROFESSIONALS */}
                <div className="border border-border rounded-xl p-5" data-testid="orphan-pros-panel">
                  <div className="flex items-center gap-2 mb-3">
                    <Broom size={22} className="text-destructive" weight="duotone" />
                    <h3 className="font-heading font-medium text-lg">Dati di utenti cancellati</h3>
                  </div>
                  <p className="text-sm text-foreground/70 mb-4">
                    Rimuove ogni traccia degli utenti che non sono più nella lista utenti: profili professionista orfani
                    (che comparivano nelle proposte ai clienti), promozioni, richieste, prenotazioni, notifiche, wallet,
                    chat, cartelle cliniche e recensioni residue. Gli utenti ancora presenti in lista non vengono toccati.
                  </p>
                  <div className="flex gap-2 flex-wrap">
                    <Button onClick={() => runOrphanCleanup(true)} variant="outline" disabled={orphanLoading} data-testid="orphan-preview-btn">
                      Cerca dati residui
                    </Button>
                    {(orphanPreview?.found > 0 || orphanRelatedTotal > 0) && (
                      <Button onClick={() => runOrphanCleanup(false)} disabled={orphanLoading} className="bg-destructive hover:bg-destructive/90 text-white" data-testid="orphan-delete-btn">
                        Elimina {orphanPreview.found + orphanRelatedTotal} elementi
                      </Button>
                    )}
                  </div>
                  {orphanPreview && (
                    <div className="mt-3 text-sm space-y-2" data-testid="orphan-preview-result">
                      {orphanPreview.found === 0 && orphanRelatedTotal === 0 ? (
                        <p className="text-emerald-700">Nessun dato residuo: il database è coerente.</p>
                      ) : (
                        <>
                          {orphanPreview.orphans?.length > 0 && (
                            <ul className="space-y-1 text-foreground/75">
                              {orphanPreview.orphans.map((o) => (
                                <li key={o.professional_id}>• {o.name} <span className="text-xs text-foreground/50">({o.professional_id})</span></li>
                              ))}
                            </ul>
                          )}
                          <div className="flex flex-wrap gap-2">
                            {Object.entries(orphanPreview.related || {}).filter(([, v]) => v > 0).map(([k, v]) => (
                              <span key={k} className="px-2 py-1 rounded-md bg-muted/60 text-xs text-foreground/70">{k}: {v}</span>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* BOOKINGS & PAYMENTS CLEANUP */}
                <div className="border border-border rounded-xl p-5" data-testid="data-cleanup-panel">
                  <div className="flex items-center gap-2 mb-3">
                    <Broom size={22} className="text-destructive" weight="duotone" />
                    <h3 className="font-heading font-medium text-lg">Pulizia Prenotazioni & Pagamenti</h3>
                  </div>
                  <p className="text-sm text-foreground/70 mb-4">
                    Rimuove prenotazioni e sessioni di pagamento residue dai test. I pagamenti "non incassati" sono le sessioni Stripe mai completate (stato ≠ paid): eliminarle non tocca gli incassi reali. Azione irreversibile.
                  </p>

                  <p className="text-xs font-medium text-foreground/70 mb-2">Prenotazioni</p>
                  <div className="flex gap-2 flex-wrap mb-4">
                    <Button onClick={() => runDataCleanup('bookings', 'test', true)} variant="outline" disabled={dataCleanupLoading} data-testid="book-cleanup-preview-test">
                      Anteprima di test
                    </Button>
                    {dataCleanupPreview?.target === 'bookings' && dataCleanupPreview.mode === 'test' && dataCleanupPreview.matched_count > 0 && (
                      <Button onClick={() => runDataCleanup('bookings', 'test', false)} disabled={dataCleanupLoading} className="bg-destructive hover:bg-destructive/90 text-white" data-testid="book-cleanup-confirm-test">
                        <Trash size={16} className="mr-1" /> Elimina {dataCleanupPreview.matched_count} di test
                      </Button>
                    )}
                    <Button onClick={() => runDataCleanup('bookings', 'orphans', false)} variant="outline" disabled={dataCleanupLoading} data-testid="book-cleanup-orphans">
                      <Trash size={16} className="mr-1" /> Prenotazioni orfane
                    </Button>
                    <Button onClick={() => runDataCleanup('bookings', 'all', false)} variant="outline" disabled={dataCleanupLoading} className="border-destructive text-destructive hover:bg-destructive/10" data-testid="book-cleanup-all">
                      <Trash size={16} className="mr-1" /> Elimina TUTTE le prenotazioni
                    </Button>
                  </div>

                  <p className="text-xs font-medium text-foreground/70 mb-2">Pagamenti</p>
                  <div className="flex gap-2 flex-wrap">
                    <Button onClick={() => runDataCleanup('payments', 'unpaid', true)} variant="outline" disabled={dataCleanupLoading} data-testid="pay-cleanup-preview-unpaid">
                      Anteprima non incassati
                    </Button>
                    {dataCleanupPreview?.target === 'payments' && dataCleanupPreview.mode === 'unpaid' && dataCleanupPreview.matched_count > 0 && (
                      <Button onClick={() => runDataCleanup('payments', 'unpaid', false)} disabled={dataCleanupLoading} className="bg-destructive hover:bg-destructive/90 text-white" data-testid="pay-cleanup-confirm-unpaid">
                        <Trash size={16} className="mr-1" /> Elimina {dataCleanupPreview.matched_count} non incassati
                      </Button>
                    )}
                    <Button onClick={() => runDataCleanup('payments', 'orphans', false)} variant="outline" disabled={dataCleanupLoading} data-testid="pay-cleanup-orphans">
                      <Trash size={16} className="mr-1" /> Pagamenti orfani
                    </Button>
                    <Button onClick={() => runDataCleanup('payments', 'all', false)} variant="outline" disabled={dataCleanupLoading} className="border-destructive text-destructive hover:bg-destructive/10" data-testid="pay-cleanup-all">
                      <Trash size={16} className="mr-1" /> Elimina TUTTI i pagamenti
                    </Button>
                  </div>
                  {dataCleanupPreview && (
                    <div className="mt-3 bg-muted/40 rounded-lg p-3 text-sm" data-testid="data-cleanup-preview">
                      {dataCleanupPreview.matched_count === 0
                        ? 'Nessun elemento trovato'
                        : `${dataCleanupPreview.matched_count} ${dataCleanupPreview.target === 'bookings' ? 'prenotazioni' : 'pagamenti'} da eliminare (${dataCleanupPreview.mode})`}
                    </div>
                  )}
                </div>

                {/* Batch review reminders */}                <div className="border border-border rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Broom size={22} className="text-primary" weight="duotone" />
                    <h3 className="font-heading font-medium text-lg">Promemoria Recensioni Verificate</h3>
                  </div>
                  <p className="text-sm text-foreground/70 mb-4">
                    Invia una mail a tutti i clienti con booking <strong>completed</strong> e <strong>paid</strong> che non hanno ancora lasciato una recensione e a cui non è già stato inviato un promemoria.
                    Idempotente: non manda promemoria doppi.
                  </p>
                  <Button
                    variant="outline"
                    onClick={async () => {
                      try {
                        const r = await api.post('/api/admin/notifications/send-review-reminders');
                        toast.success(`Promemoria inviati: ${r.data.sent} · Con recensione: ${r.data.skipped_with_review} · Errori: ${r.data.errors}`);
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Errore invio');
                      }
                    }}
                    data-testid="review-reminders-btn"
                  >
                    Invia promemoria recensioni
                  </Button>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </Card>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
